// ============================================================================
// Discovery tab — sources catalog + raw lead inbox + CSV/manual import.
//
// This is PLUMBING, not a scraper: the catalog holds where subs can be found
// (registries, SBA data, directories, chambers, job boards), each tagged with
// access_type and tos_scrape_allowed. Sources whose ToS prohibit scraping
// (Google/LinkedIn/Thumbtack/Angi/Craigslist/Facebook/Route) are manual-entry
// only — the importer works from what a human collected. One-click triage
// promotes a lead into the pipeline as channel=Discovery.
//
// CSV import: header row required; recognized columns (case-insensitive):
// business_name, owner_name, market, phone, email. Everything else is kept
// in raw_json so no imported data is lost.
// ============================================================================

import { useMemo, useRef, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  FileUp,
  Plus,
  Search,
  ShieldAlert,
} from 'lucide-react';
import { useAuth } from '../../shared/auth';
import { EmptyState, ErrorState, Skeleton } from '../../shared/components';
import type { DiscoveredLead, UseDiscovery } from './hooks/use-sourcing-data';
import { DISCOVERY_ENTITY_TYPES } from './hooks/use-sourcing-data';
import { marketOptions, useMarkets } from '../../shared/markets';
import { LeadDrawer } from './lead-drawer';
import { alreadyOnFileFrom } from '../subcontractors/already-on-file';
import { errorMessage } from '../../shared/errors';

interface Props {
  discovery: UseDiscovery;
  /** Promote a lead into the pipeline; the caller also refetches prospects. */
  onPromote: (lead: DiscoveredLead, addAnyway?: boolean) => Promise<void>;
}

const ENRICHMENT_FILTER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Any enrichment' },
  { value: 'none', label: 'Not enriched' },
  { value: 'queued', label: 'Queued' },
  { value: 'enriched', label: 'Enriched' },
  { value: 'no_match', label: 'No match' },
  { value: 'error', label: 'Error' },
];

const JOBS_BUCKET_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Any size' },
  { value: '1', label: '1 job' },
  { value: '2-4', label: '2–4 jobs' },
  { value: '5-9', label: '5–9 jobs' },
  { value: '10+', label: '10+ jobs' },
];

const ENRICHMENT_BADGE: Record<
  DiscoveredLead['enrichment_status'],
  { label: string; cls: string }
> = {
  none: { label: 'New', cls: 'bg-tcs-surface text-tcs-text-muted' },
  queued: { label: 'Queued', cls: 'bg-st-warn-bg-strong text-st-warn-fg-strong' },
  enriched: { label: 'Enriched', cls: 'bg-st-ok-bg-strong text-st-ok-fg-strong' },
  no_match: { label: 'No match', cls: 'bg-tcs-surface text-tcs-text-muted' },
  error: { label: 'Error', cls: 'bg-st-danger-bg-strong text-st-danger-fg' },
};

const KNOWN_COLS = ['business_name', 'owner_name', 'market', 'phone', 'email'] as const;

function parseCsv(text: string): Array<Record<string, string>> {
  // Minimal CSV parser: handles quoted fields with commas + escaped quotes.
  const rows: string[][] = [];
  let cur = '';
  let row: string[] = [];
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') {
        cur += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(cur);
      cur = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(cur);
      cur = '';
      if (row.some((c) => c.trim() !== '')) rows.push(row);
      row = [];
    } else {
      cur += ch;
    }
  }
  row.push(cur);
  if (row.some((c) => c.trim() !== '')) rows.push(row);
  if (rows.length < 2) return [];
  const headers = rows[0].map((h) => h.trim().toLowerCase().replaceAll(' ', '_'));
  return rows.slice(1).map((r) => {
    const obj: Record<string, string> = {};
    headers.forEach((h, idx) => {
      obj[h] = (r[idx] ?? '').trim();
    });
    return obj;
  });
}

export function DiscoveryTab({ discovery, onPromote }: Props) {
  // The market filter is the master market list (item 1052, Ben 2026-11-02).
  // Leads are stored under the market's own name ("Houston Metro"); the typed
  // short names this replaced ("Houston") matched no lead at all.
  const { markets } = useMarkets();
  const marketNames = useMemo(() => marketOptions(markets), [markets]);
  const {
    sources,
    leads,
    totalCount,
    isLoading,
    error,
    filters,
    setFilter,
    page,
    setPage,
    pageSize,
    refetch,
    addLeads,
    discardLead,
    enrichLead,
  } = discovery;
  const { employee } = useAuth();
  const eid = employee?.employee_id ?? null;
  const [selectedSource, setSelectedSource] = useState<string>('');
  const [showManual, setShowManual] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Re-resolve the open lead from the live page so a Dig-deeper refetch
  // reflects the new enrichment_status without closing the drawer.
  const selectedLead = useMemo(
    () => leads.find((l) => l.lead_id === selectedId) ?? null,
    [leads, selectedId],
  );

  const rangeStart = totalCount === 0 ? 0 : page * pageSize + 1;
  const rangeEnd = page * pageSize + leads.length;
  const hasPrev = page > 0;
  const hasNext = (page + 1) * pageSize < totalCount;

  const run = async (fn: () => Promise<unknown>, okMsg: string) => {
    setBusy(true);
    setMsg(null);
    try {
      await fn();
      setMsg(okMsg);
    } catch (e) {
      setMsg(errorMessage(e, 'Failed.'));
    } finally {
      setBusy(false);
    }
  };

  const handleCsv = async (file: File) => {
    const text = await file.text();
    const parsed = parseCsv(text);
    if (parsed.length === 0) {
      setMsg('No data rows found — the CSV needs a header row plus at least one row.');
      return;
    }
    const rows: Array<Partial<DiscoveredLead>> = parsed.map((r) => {
      const extra: Record<string, string> = {};
      for (const [k, v] of Object.entries(r)) {
        if (!(KNOWN_COLS as readonly string[]).includes(k) && v) extra[k] = v;
      }
      return {
        source_id: selectedSource || null,
        business_name: r.business_name || r.company || r.name || '(unnamed)',
        owner_name: r.owner_name || null,
        market: r.market || null,
        phone: r.phone || null,
        email: r.email || null,
        raw_json: Object.keys(extra).length ? extra : null,
      };
    });
    await run(() => addLeads(rows, eid), `Imported ${rows.length} lead${rows.length === 1 ? '' : 's'}.`);
  };

  return (
    <div className="space-y-6">
      {/* import bar */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-tcs-border p-3">
        <select
          value={selectedSource}
          onChange={(e) => setSelectedSource(e.target.value)}
          className="rounded-md border border-tcs-border bg-tcs-bg px-2 py-1.5 text-sm"
        >
          <option value="">Tag imports to a source…</option>
          {sources.map((s) => (
            <option key={s.source_id} value={s.source_id}>
              {s.name}
            </option>
          ))}
        </select>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleCsv(f);
            e.target.value = '';
          }}
        />
        <button
          type="button"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
          className="inline-flex items-center gap-1.5 rounded-md border border-tcs-border px-3 py-1.5 text-sm font-medium hover:bg-tcs-bg-muted"
        >
          <FileUp className="h-4 w-4" /> Import CSV
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => setShowManual((v) => !v)}
          className="inline-flex items-center gap-1.5 rounded-md border border-tcs-border px-3 py-1.5 text-sm font-medium hover:bg-tcs-bg-muted"
        >
          <Plus className="h-4 w-4" /> Manual entry
        </button>
        {msg && <span className="text-xs text-tcs-text-muted">{msg}</span>}
      </div>

      {showManual && (
        <ManualLeadForm
          disabled={busy}
          onSubmit={(lead) =>
            run(
              () => addLeads([{ ...lead, source_id: selectedSource || null }], eid),
              'Lead added.',
            ).then(() => setShowManual(false))
          }
        />
      )}

      {/* lead inbox — filter bar */}
      <section>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-tcs-text-muted" />
            <input
              type="search"
              value={filters.search}
              onChange={(e) => setFilter('search', e.target.value)}
              placeholder="Search business or city…"
              className="w-56 rounded-md border border-tcs-border bg-tcs-bg py-1.5 pl-7 pr-2 text-sm"
            />
          </div>
          <select
            value={filters.market}
            onChange={(e) => setFilter('market', e.target.value)}
            className="rounded-md border border-tcs-border bg-tcs-bg px-2 py-1.5 text-sm"
          >
            <option value="">All markets</option>
            {marketNames.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
          <select
            value={filters.jobsBucket}
            onChange={(e) => setFilter('jobsBucket', e.target.value as typeof filters.jobsBucket)}
            className="rounded-md border border-tcs-border bg-tcs-bg px-2 py-1.5 text-sm"
          >
            {JOBS_BUCKET_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            value={filters.entityType}
            onChange={(e) => setFilter('entityType', e.target.value)}
            className="rounded-md border border-tcs-border bg-tcs-bg px-2 py-1.5 text-sm"
          >
            <option value="">All entity types</option>
            {DISCOVERY_ENTITY_TYPES.map((t) => (
              <option key={t} value={t}>
                {t.replace(/\s+/g, ' ')}
              </option>
            ))}
          </select>
          <select
            value={filters.source}
            onChange={(e) => setFilter('source', e.target.value)}
            className="rounded-md border border-tcs-border bg-tcs-bg px-2 py-1.5 text-sm"
          >
            <option value="">All sources</option>
            {sources.map((s) => (
              <option key={s.source_id} value={s.source_id}>
                {s.name}
              </option>
            ))}
          </select>
          <select
            value={filters.nameKind}
            onChange={(e) => setFilter('nameKind', e.target.value as typeof filters.nameKind)}
            className="rounded-md border border-tcs-border bg-tcs-bg px-2 py-1.5 text-sm"
          >
            <option value="">Company or personal</option>
            <option value="company">Company name</option>
            <option value="personal">Personal name</option>
          </select>
          <select
            value={filters.contact}
            onChange={(e) => setFilter('contact', e.target.value as typeof filters.contact)}
            className="rounded-md border border-tcs-border bg-tcs-bg px-2 py-1.5 text-sm"
          >
            <option value="">Any contact</option>
            <option value="has">Has phone/email</option>
          </select>
          <select
            value={filters.enrichmentStatus}
            onChange={(e) =>
              setFilter('enrichmentStatus', e.target.value as typeof filters.enrichmentStatus)
            }
            className="rounded-md border border-tcs-border bg-tcs-bg px-2 py-1.5 text-sm"
          >
            {ENRICHMENT_FILTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* results header */}
        <div className="mb-2 flex items-center justify-between text-xs text-tcs-text-muted">
          <span>
            <span className="font-semibold text-tcs-text">{totalCount.toLocaleString()}</span> leads
            {totalCount > 0 && ` · showing ${rangeStart.toLocaleString()}–${rangeEnd.toLocaleString()}`}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={!hasPrev || isLoading}
              onClick={() => setPage(page - 1)}
              className="inline-flex items-center gap-0.5 rounded-md border border-tcs-border px-2 py-1 font-medium hover:bg-tcs-bg-muted disabled:opacity-40"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </button>
            <button
              type="button"
              disabled={!hasNext || isLoading}
              onClick={() => setPage(page + 1)}
              className="inline-flex items-center gap-0.5 rounded-md border border-tcs-border px-2 py-1 font-medium hover:bg-tcs-bg-muted disabled:opacity-40"
            >
              Next <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* results list */}
        {error ? (
          <ErrorState body={error} onRetry={refetch} />
        ) : isLoading ? (
          <div className="space-y-1.5">
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : leads.length === 0 ? (
          <EmptyState
            title="No leads match"
            body="Adjust the filters above, or import a CSV / add leads manually — they land here for triage."
          />
        ) : (
          <ul className="divide-y divide-tcs-border rounded-lg border border-tcs-border">
            {leads.map((l) => {
              const badge = ENRICHMENT_BADGE[l.enrichment_status];
              const meta = [
                [l.city_name, l.market].filter(Boolean).join(', '),
                l.ppp_jobs !== null ? `${l.ppp_jobs} jobs` : null,
                l.entity_type ? l.entity_type.replace(/\s+/g, ' ') : null,
              ].filter(Boolean);
              return (
                <li key={l.lead_id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(l.lead_id)}
                    className="flex w-full items-center gap-2 p-3 text-left hover:bg-tcs-bg-muted"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-tcs-text">
                        {l.business_name}
                      </div>
                      <div className="truncate text-xs text-tcs-text-muted">
                        {meta.join(' · ') || '—'}
                      </div>
                    </div>
                    <span
                      className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${badge.cls}`}
                    >
                      {badge.label}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* sources catalog */}
      <section>
        <h3 className="mb-2 text-sm font-semibold text-tcs-text">Sources catalog</h3>
        <div className="overflow-x-auto rounded-lg border border-tcs-border">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-tcs-border text-[10px] uppercase tracking-wide text-tcs-text-muted">
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2">Category</th>
                <th className="px-3 py-2">Coverage</th>
                <th className="px-3 py-2">Access</th>
                <th className="px-3 py-2">Cost</th>
                <th className="px-3 py-2">Scrape OK?</th>
              </tr>
            </thead>
            <tbody>
              {sources.map((s) => (
                <tr key={s.source_id} className="border-b border-tcs-border/60 last:border-0">
                  <td className="px-3 py-2">
                    <div className="font-medium text-tcs-text">{s.name}</div>
                    {s.notes && <div className="text-xs text-tcs-text-muted">{s.notes}</div>}
                  </td>
                  <td className="px-3 py-2 text-xs">{s.category.replaceAll('_', ' ')}</td>
                  <td className="px-3 py-2 text-xs">{s.market_coverage.join(', ')}</td>
                  <td className="px-3 py-2 text-xs">{s.access_type}</td>
                  <td className="px-3 py-2 text-xs">{s.cost_note ?? '—'}</td>
                  <td className="px-3 py-2">
                    {s.tos_scrape_allowed ? (
                      <span className="text-xs text-st-ok-fg">Yes</span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 text-xs text-st-warn-fg"
                        title="Terms of service prohibit scraping/bulk export — manual entry only."
                      >
                        <ShieldAlert className="h-3.5 w-3.5" /> Manual only
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {selectedLead && (
        <LeadDrawer
          lead={selectedLead}
          sources={sources}
          busy={busy}
          onClose={() => setSelectedId(null)}
          onPromote={async (lead, addAnyway) => {
            // "Already on file?" stays in the drawer, where the choice is made.
            setBusy(true);
            setMsg(null);
            try {
              await onPromote(lead, addAnyway);
              setMsg('Promoted to pipeline.');
              setSelectedId(null);
            } catch (e) {
              if (alreadyOnFileFrom(e)) throw e;
              setMsg(errorMessage(e, 'Failed.'));
              setSelectedId(null);
            } finally {
              setBusy(false);
            }
          }}
          onDiscard={async (leadId) => {
            await run(() => discardLead(leadId), 'Discarded.');
            setSelectedId(null);
          }}
          onDigDeeper={(leadId) => run(() => enrichLead(leadId), 'Queued for enrichment.')}
        />
      )}
    </div>
  );
}

function ManualLeadForm({
  disabled,
  onSubmit,
}: {
  disabled: boolean;
  onSubmit: (lead: Partial<DiscoveredLead>) => void;
}) {
  const [f, setF] = useState({ business_name: '', owner_name: '', market: '', phone: '', email: '' });
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setF((v) => ({ ...v, [k]: e.target.value }));
  return (
    <form
      className="grid gap-2 rounded-lg border border-tcs-border p-3 sm:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (!f.business_name.trim()) return;
        onSubmit({
          business_name: f.business_name.trim(),
          owner_name: f.owner_name.trim() || null,
          market: f.market.trim() || null,
          phone: f.phone.trim() || null,
          email: f.email.trim() || null,
        });
      }}
    >
      <input required placeholder="Business name *" value={f.business_name} onChange={set('business_name')} className="rounded-md border border-tcs-border px-2 py-1.5 text-sm" />
      <input placeholder="Owner name" value={f.owner_name} onChange={set('owner_name')} className="rounded-md border border-tcs-border px-2 py-1.5 text-sm" />
      <input placeholder="Market (e.g. Denver)" value={f.market} onChange={set('market')} className="rounded-md border border-tcs-border px-2 py-1.5 text-sm" />
      <input placeholder="Phone" value={f.phone} onChange={set('phone')} className="rounded-md border border-tcs-border px-2 py-1.5 text-sm" />
      <input placeholder="Email" type="email" value={f.email} onChange={set('email')} className="rounded-md border border-tcs-border px-2 py-1.5 text-sm" />
      <button
        type="submit"
        disabled={disabled}
        className="rounded-md bg-tcs-inverse px-3 py-1.5 text-sm font-semibold text-tcs-inverse-ink hover:opacity-90"
      >
        Add lead
      </button>
    </form>
  );
}
